import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  avgDays,
  buildDepartmentRows,
  buildWeekTotals,
  deriveDayRevenue,
  formatCurrency,
  formatCurrencyFull,
  formatHours,
  formatPercent,
  loadCategoryVisibility,
  saveCategoryVisibility,
  sumDays,
} from './weeklyLaborDashboardUtils';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import { harmonateCardSx, harmonateTableHeaderBarSx } from '../../../theme/harmonateStyles';

const CATEGORY_STYLES = {
  budget: { bg: '#dbeafe', text: '#1d4ed8', chart: '#2563eb', label: 'Budget' },
  forecast: { bg: '#dcfce7', text: ht.green, chart: '#dc2626', label: 'Forecast' },
  scheduled: { bg: '#fef3c7', text: '#b45309', chart: '#d97706', label: 'Scheduled' },
  actual: { bg: '#f3e8ff', text: '#7e22ce', chart: ht.green, label: 'Actual' },
};

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    'Content-Type': 'application/json',
  };
};

const SplitKpiRow = ({ label, values, visibility }) => {
  const visibleKeys = Object.keys(CATEGORY_STYLES).filter((key) => visibility[key]);
  if (!visibleKeys.length) {
    return (
      <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 1, px: 1, py: 1.25, textAlign: 'center' }}>
        <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8' }}>Select a category</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(${visibleKeys.length}, 1fr)`,
        borderRadius: 1,
        overflow: 'hidden',
        border: '1px solid #e5e7eb',
        height: '100%',
      }}
    >
      {visibleKeys.map((key) => {
        const style = CATEGORY_STYLES[key];
        return (
          <Box key={key} sx={{ bgcolor: style.bg, px: 0.75, py: 0.75, textAlign: 'center' }}>
            <Typography sx={{ color: style.text, fontWeight: 600, display: 'block', fontSize: '0.65rem', lineHeight: 1.2 }}>
              {style.label} {label}
            </Typography>
            <Typography sx={{ fontWeight: 700, color: '#111827', fontSize: '0.85rem', mt: 0.15, lineHeight: 1.2 }}>
              {values[key]}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

const MetaStat = ({ label, value }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'baseline',
      gap: 0.5,
      px: 1,
      py: 0.25,
      bgcolor: ht.white,
      border: `1px solid ${ht.line}`,
      borderRadius: ht.buttonRadius,
    }}
  >
    <Typography sx={{ color: ht.text, fontWeight: 600, fontSize: '0.65rem' }}>
      {label}
    </Typography>
    <Typography sx={{ color: ht.text, fontWeight: 700, fontSize: '0.75rem' }}>
      {value}
    </Typography>
  </Box>
);

const DashboardTable = ({ title, children, sx = {} }) => (
  <Paper
    elevation={0}
    sx={{
      ...harmonateCardSx,
      bgcolor: ht.white,
      mb: 1.5,
      minWidth: 0,
      ...sx,
    }}
  >
    <Box sx={{ ...harmonateTableHeaderBarSx, px: 1.5, py: 0.6 }}>
      <Typography sx={{ color: ht.white, fontWeight: 700, fontSize: '0.8rem' }}>
        {title}
      </Typography>
    </Box>
    <Box sx={{ overflowX: 'auto', bgcolor: ht.white }}>{children}</Box>
  </Paper>
);

const headerCellSx = {
  fontWeight: 700,
  fontSize: '0.68rem',
  bgcolor: ht.listBg,
  color: ht.text,
  whiteSpace: 'nowrap',
  py: 0.5,
  px: 1,
  lineHeight: 1.2,
};

const bodyCellSx = {
  fontSize: '0.72rem',
  py: 0.4,
  px: 1,
  lineHeight: 1.2,
};

const StatusChip = ({ status }) => {
  const color = status === 'Stress' ? '#dc2626' : status === 'Under' ? '#2563eb' : '#16a34a';
  return (
    <Typography sx={{ color, fontWeight: 700, fontSize: '0.72rem' }}>
      {status}
    </Typography>
  );
};

const WeeklyLaborDashboard = ({
  tenantId,
  propertyId,
  propertyName,
  weekStartDate,
  weekEndDate,
  weekDates,
  refreshKey = 0,
}) => {
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState([]);
  const [roles, setRoles] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [departmentLabor, setDepartmentLabor] = useState([]);
  const [roomCount, setRoomCount] = useState(null);
  const [categoryVisibility, setCategoryVisibility] = useState(() => loadCategoryVisibility());

  const handleCategoryToggle = (key) => {
    setCategoryVisibility((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      const anyOn = Object.values(next).some(Boolean);
      if (!anyOn) return prev;
      saveCategoryVisibility(next);
      return next;
    });
  };

  const fetchDashboard = useCallback(async () => {
    if (!tenantId || !propertyId || !weekStartDate) {
      setDays([]);
      setRoles([]);
      setBudgets([]);
      setDepartmentLabor([]);
      return;
    }

    const base = process.env.REACT_APP_BACKEND_URL || '';
    setLoading(true);
    try {
      // Budget + Forecast $ / hours come from Snowflake via workforce-week-summary.
      // Demand fields still Postgres. Scheduled / actual paths unchanged.
      const [summaryRes, propertyRes, rolesRes] = await Promise.all([
        fetch(
          `${base}conductor/workforce-week-summary?tenant_id=${encodeURIComponent(tenantId)}` +
            `&property_id=${encodeURIComponent(propertyId)}` +
            `&week_start_date=${encodeURIComponent(weekStartDate)}`,
          { method: 'GET', headers: authHeaders() }
        ),
        fetch(`${base}conductor/cash-recon/properties/${encodeURIComponent(propertyId)}`, {
          method: 'GET',
          headers: authHeaders(),
        }),
        fetch(
          `${base}conductor/role-positions/by-tenant?tenant_id=${encodeURIComponent(tenantId)}` +
            `&property_id=${encodeURIComponent(propertyId)}`,
          { method: 'GET', headers: authHeaders() }
        ),
      ]);

      if (summaryRes.ok) {
        const summary = await summaryRes.json();
        setDays(Array.isArray(summary?.days) ? summary.days : []);
        setDepartmentLabor(Array.isArray(summary?.departmentLabor) ? summary.departmentLabor : []);
      } else {
        setDays([]);
        setDepartmentLabor([]);
      }

      if (propertyRes.ok) {
        const property = await propertyRes.json();
        setRoomCount(property?.roomCount ?? property?.room_count ?? null);
      } else {
        setRoomCount(null);
      }

      if (rolesRes.ok) {
        const roleData = await rolesRes.json();
        setRoles(Array.isArray(roleData) ? roleData : []);
      } else {
        setRoles([]);
      }

      setBudgets([]);
    } catch (err) {
      console.error(err);
      setDays([]);
      setRoles([]);
      setBudgets([]);
      setDepartmentLabor([]);
    } finally {
      setLoading(false);
    }
  }, [tenantId, propertyId, weekStartDate]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard, refreshKey]);

  const weekLabel = useMemo(() => {
    if (!weekDates?.length) return '';
    const start = weekDates[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const end = weekDates[weekDates.length - 1].toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `Week of ${start} - ${end}`;
  }, [weekDates]);

  const totals = useMemo(() => buildWeekTotals(days), [days]);
  const departmentRows = useMemo(
    () => buildDepartmentRows(roles, budgets, days, totals, departmentLabor),
    [roles, budgets, days, totals, departmentLabor]
  );

  const dailyRows = useMemo(
    () =>
      (weekDates || []).map((date, idx) => {
        const iso = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        const day = days.find((d) => d.businessDate === iso) || days[idx] || {};
        const revenue = deriveDayRevenue(day);
        return {
          day: date.toLocaleDateString('en-US', { weekday: 'short' }),
          ...revenue,
          budgetLabor: Number(day?.budgetedLaborCost) || 0,
          forecastLabor: Number(day?.forecastedLaborCost) || 0,
          scheduledLabor: Number(day?.scheduledLaborCost) || 0,
          actualLabor: Number(day?.actualLaborCost) || 0,
          budgetHours: Number(day?.budgetedHours) || 0,
          forecastHours: Number(day?.forecastedHours) || 0,
          scheduledHours: Number(day?.scheduledLaborHours) || 0,
          actualHours: Number(day?.actualLaborHours) || 0,
        };
      }),
    [weekDates, days]
  );

  const departmentChartData = useMemo(
    () =>
      departmentRows.map((row) => ({
        name: row.department.length > 12 ? `${row.department.slice(0, 12)}…` : row.department,
        budget: Math.round(row.budgetLabor),
        forecast: Math.round(row.forecastLabor),
        scheduled: Math.round(row.scheduledLabor),
        actual: Math.round(row.actualLabor),
      })),
    [departmentRows]
  );

  const avgOccupancy = avgDays(days, 'occupancyPercent');
  const totalOccupiedRooms = sumDays(days, 'occupiedRooms');
  const adrBaseRevenue = totals.scheduledRevenue || totals.forecastRevenue || totals.actualRevenue;
  const adr = totalOccupiedRooms > 0 ? adrBaseRevenue / totalOccupiedRooms : 0;

  const show = categoryVisibility;

  if (!propertyId) {
    return (
      <Paper elevation={0} sx={{ ...harmonateCardSx, bgcolor: ht.white, p: 3, mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Select a business unit to view the weekly labor dashboard.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Paper elevation={0} sx={{ ...harmonateCardSx, bgcolor: ht.white, mb: 1.5 }}>
        <Box
          sx={{
            ...harmonateTableHeaderBarSx,
            px: 1.5,
            py: 0.75,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.2 }}>
              {propertyName || 'Property'}
            </Typography>
            <Typography sx={{ color: ht.white, fontWeight: 500, fontSize: '0.7rem' }}>
              Weekly Labor Dashboard
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ bgcolor: 'rgba(255,255,255,0.12)', px: 1.25, py: 0.25, borderRadius: 999, fontWeight: 600, fontSize: '0.7rem' }}>
              {weekLabel}
            </Typography>
            {loading ? <CircularProgress size={14} sx={{ color: ht.white }} /> : null}
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            px: 1.5,
            py: 0.75,
            bgcolor: ht.listBg,
            borderBottom: `1px solid ${ht.line}`,
          }}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            <MetaStat label="Rooms" value={roomCount ?? '—'} />
            <MetaStat label="Occupancy" value={formatPercent(avgOccupancy)} />
            <MetaStat label="ADR" value={formatCurrency(adr)} />
            <MetaStat label="Occupied Rooms" value={totalOccupiedRooms.toLocaleString('en-US')} />
          </Box>
          <FormGroup row sx={{ gap: 0.25, flexWrap: 'wrap' }}>
            {Object.entries(CATEGORY_STYLES).map(([key, style]) => (
              <FormControlLabel
                key={key}
                control={
                  <Checkbox
                    size="small"
                    checked={Boolean(categoryVisibility[key])}
                    onChange={() => handleCategoryToggle(key)}
                    sx={{
                      color: style.chart,
                      '&.Mui-checked': { color: style.chart },
                      py: 0.25,
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: style.text }}>
                    {style.label}
                  </Typography>
                }
                sx={{ mr: 0.75, ml: 0 }}
              />
            ))}
          </FormGroup>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr 1fr' },
            gap: 1,
            px: 1.25,
            py: 1.25,
          }}
        >
          <SplitKpiRow
            label="Revenue $"
            visibility={show}
            values={{
              budget: formatCurrencyFull(totals.budgetRevenue),
              forecast: formatCurrencyFull(totals.forecastRevenue),
              scheduled: formatCurrencyFull(totals.scheduledRevenue),
              actual: formatCurrencyFull(totals.actualRevenue),
            }}
          />
          <SplitKpiRow
            label="Labor $"
            visibility={show}
            values={{
              budget: formatCurrencyFull(totals.budgetLabor),
              forecast: formatCurrencyFull(totals.forecastLabor),
              scheduled: formatCurrencyFull(totals.scheduledLabor),
              actual: formatCurrencyFull(totals.actualLabor),
            }}
          />
          <SplitKpiRow
            label="Hours"
            visibility={show}
            values={{
              budget: formatHours(totals.budgetHours),
              forecast: formatHours(totals.forecastHours),
              scheduled: formatHours(totals.scheduledHours),
              actual: formatHours(totals.actualHours),
            }}
          />
          <SplitKpiRow
            label="Labor %"
            visibility={show}
            values={{
              budget: formatPercent(totals.budgetLaborPct),
              forecast: formatPercent(totals.forecastLaborPct),
              scheduled: formatPercent(totals.scheduledLaborPct),
              actual: formatPercent(totals.actualLaborPct),
            }}
          />
        </Box>
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
          gap: 1.5,
          mb: 1.5,
          alignItems: 'stretch',
        }}
      >
        <DashboardTable title="Department Labor Summary" sx={{ mb: 0, height: '100%' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>Department</TableCell>
                {show.budget ? <TableCell align="right" sx={headerCellSx}>Budget Labor $</TableCell> : null}
                {show.forecast ? <TableCell align="right" sx={headerCellSx}>Forecast Labor $</TableCell> : null}
                {show.scheduled ? <TableCell align="right" sx={headerCellSx}>Scheduled Labor $</TableCell> : null}
                {show.actual ? <TableCell align="right" sx={headerCellSx}>Actual Labor $</TableCell> : null}
                {show.budget ? <TableCell align="right" sx={headerCellSx}>Budget Hours</TableCell> : null}
                {show.forecast ? <TableCell align="right" sx={headerCellSx}>Forecast Hours</TableCell> : null}
                {show.scheduled ? <TableCell align="right" sx={headerCellSx}>Scheduled Hours</TableCell> : null}
                {show.actual ? <TableCell align="right" sx={headerCellSx}>Actual Hours</TableCell> : null}
                {show.scheduled ? <TableCell align="right" sx={headerCellSx}>Scheduled MPOR</TableCell> : null}
                {show.forecast && show.scheduled ? <TableCell align="right" sx={headerCellSx}>vs Forecast $</TableCell> : null}
                {show.forecast && show.scheduled ? <TableCell align="center" sx={headerCellSx}>Status</TableCell> : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {departmentRows.map((row) => (
                <TableRow key={row.department} hover>
                  <TableCell sx={{ ...bodyCellSx, fontWeight: 600, whiteSpace: 'nowrap' }}>{row.department}</TableCell>
                  {show.budget ? <TableCell align="right" sx={bodyCellSx}>{formatCurrency(row.budgetLabor)}</TableCell> : null}
                  {show.forecast ? <TableCell align="right" sx={bodyCellSx}>{formatCurrency(row.forecastLabor)}</TableCell> : null}
                  {show.scheduled ? <TableCell align="right" sx={bodyCellSx}>{formatCurrency(row.scheduledLabor)}</TableCell> : null}
                  {show.actual ? <TableCell align="right" sx={bodyCellSx}>{formatCurrency(row.actualLabor)}</TableCell> : null}
                  {show.budget ? <TableCell align="right" sx={bodyCellSx}>{formatHours(row.budgetHours)}</TableCell> : null}
                  {show.forecast ? <TableCell align="right" sx={bodyCellSx}>{formatHours(row.forecastHours)}</TableCell> : null}
                  {show.scheduled ? <TableCell align="right" sx={bodyCellSx}>{formatHours(row.scheduledHours)}</TableCell> : null}
                  {show.actual ? <TableCell align="right" sx={bodyCellSx}>{formatHours(row.actualHours)}</TableCell> : null}
                  {show.scheduled ? <TableCell align="right" sx={bodyCellSx}>{row.mpor > 0 ? row.mpor.toFixed(1) : '—'}</TableCell> : null}
                  {show.forecast && show.scheduled ? <TableCell align="right" sx={bodyCellSx}>{formatCurrency(row.variance)}</TableCell> : null}
                  {show.forecast && show.scheduled ? (
                    <TableCell align="center" sx={bodyCellSx}>
                      <StatusChip status={row.status} />
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DashboardTable>

        <DashboardTable title="Daily Revenue & Labor" sx={{ mb: 0, height: '100%' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>Day</TableCell>
                {show.budget ? <TableCell align="right" sx={headerCellSx}>Budget Revenue $</TableCell> : null}
                {show.forecast ? <TableCell align="right" sx={headerCellSx}>Forecast Revenue $</TableCell> : null}
                {show.scheduled ? <TableCell align="right" sx={headerCellSx}>Scheduled Revenue $</TableCell> : null}
                {show.actual ? <TableCell align="right" sx={headerCellSx}>Actual Revenue $</TableCell> : null}
                {show.budget ? <TableCell align="right" sx={headerCellSx}>Budget Labor $</TableCell> : null}
                {show.forecast ? <TableCell align="right" sx={headerCellSx}>Forecast Labor $</TableCell> : null}
                {show.scheduled ? <TableCell align="right" sx={headerCellSx}>Scheduled Labor $</TableCell> : null}
                {show.actual ? <TableCell align="right" sx={headerCellSx}>Actual Labor $</TableCell> : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {dailyRows.map((row) => (
                <TableRow key={row.day} hover>
                  <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{row.day}</TableCell>
                  {show.budget ? <TableCell align="right" sx={bodyCellSx}>{formatCurrency(row.budgetRevenue)}</TableCell> : null}
                  {show.forecast ? <TableCell align="right" sx={bodyCellSx}>{formatCurrency(row.forecastRevenue)}</TableCell> : null}
                  {show.scheduled ? <TableCell align="right" sx={bodyCellSx}>{formatCurrency(row.scheduledRevenue)}</TableCell> : null}
                  {show.actual ? <TableCell align="right" sx={bodyCellSx}>{formatCurrency(row.actualRevenue)}</TableCell> : null}
                  {show.budget ? <TableCell align="right" sx={bodyCellSx}>{formatCurrency(row.budgetLabor)}</TableCell> : null}
                  {show.forecast ? <TableCell align="right" sx={bodyCellSx}>{formatCurrency(row.forecastLabor)}</TableCell> : null}
                  {show.scheduled ? <TableCell align="right" sx={bodyCellSx}>{formatCurrency(row.scheduledLabor)}</TableCell> : null}
                  {show.actual ? <TableCell align="right" sx={bodyCellSx}>{formatCurrency(row.actualLabor)}</TableCell> : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DashboardTable>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 1.5 }}>
        <Paper elevation={0} sx={{ ...harmonateCardSx, bgcolor: ht.white, p: 1.25 }}>
          <Typography sx={{ fontWeight: 700, mb: 0.75, fontSize: '0.8rem', color: ht.text }}>
            Department Labor $ — Budget vs Forecast vs Actual
          </Typography>
          <Box sx={{ width: '100%', height: 180 }}>
            <ResponsiveContainer>
              <BarChart data={departmentChartData} margin={{ top: 4, right: 4, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 9 }} />
                <YAxis tick={{ fontSize: 9 }} width={40} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {show.budget ? <Bar dataKey="budget" name="Budget" fill={CATEGORY_STYLES.budget.chart} radius={[2, 2, 0, 0]} /> : null}
                {show.forecast ? <Bar dataKey="forecast" name="Forecast" fill={CATEGORY_STYLES.forecast.chart} radius={[2, 2, 0, 0]} /> : null}
                {show.scheduled ? <Bar dataKey="scheduled" name="Scheduled" fill={CATEGORY_STYLES.scheduled.chart} radius={[2, 2, 0, 0]} /> : null}
                {show.actual ? <Bar dataKey="actual" name="Actual" fill={CATEGORY_STYLES.actual.chart} radius={[2, 2, 0, 0]} /> : null}
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>

        <Paper elevation={0} sx={{ ...harmonateCardSx, bgcolor: ht.white, p: 1.25 }}>
          <Typography sx={{ fontWeight: 700, mb: 0.75, fontSize: '0.8rem', color: ht.text }}>
            Daily Labor $ Trend
          </Typography>
          <Box sx={{ width: '100%', height: 180 }}>
            <ResponsiveContainer>
              <LineChart data={dailyRows} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" tick={{ fontSize: 9 }} />
                <YAxis tick={{ fontSize: 9 }} width={40} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {show.budget ? <Line type="monotone" dataKey="budgetLabor" name="Budget" stroke={CATEGORY_STYLES.budget.chart} strokeWidth={1.5} dot={false} /> : null}
                {show.forecast ? <Line type="monotone" dataKey="forecastLabor" name="Forecast" stroke={CATEGORY_STYLES.forecast.chart} strokeWidth={1.5} dot={false} /> : null}
                {show.scheduled ? <Line type="monotone" dataKey="scheduledLabor" name="Scheduled" stroke={CATEGORY_STYLES.scheduled.chart} strokeWidth={1.5} dot={false} /> : null}
                {show.actual ? <Line type="monotone" dataKey="actualLabor" name="Actual" stroke={CATEGORY_STYLES.actual.chart} strokeWidth={1.5} dot={false} /> : null}
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default WeeklyLaborDashboard;
